/**
 * GDSProgressManager.js
 * Manages the visual state of the progress indicator bar.
 */
export class GDSProgressManager {
    constructor(gdsBuilderElement) {
        this.builderElement = gdsBuilderElement;
        this.progressContainer = this.builderElement.querySelector('[data-progress-container]');
        this.progressLine = this.builderElement.querySelector('#progress-line');
    }

    updateProgress(currentStep) {
        const completedSteps = [];
        for (let i = 1; i < currentStep; i++) {
            completedSteps.push(i);
        }
        this.update(currentStep, completedSteps, false);
    }

    /**
     * Updates the entire progress indicator based on the current state.
     * @param {number} currentStep - The current active step (1, 2, or 3).
     * @param {Array<number>} completedSteps - An array of completed step numbers.
     * @param {boolean} wasStep2Skipped - Flag indicating if step 2 was bypassed.
     */
    update(currentStep, completedSteps, wasStep2Skipped) {
        if (!this.progressContainer) {
            return;
        }

        // Ensure step 2 is visually "complete" if it was skipped and we are on step 3
        const effectiveCompleted = (wasStep2Skipped && currentStep === 3)
            ? [...new Set([...completedSteps, 1, 2])] // Add 1 and 2 to completed
            : completedSteps;

        this._updateProgressLine(currentStep);
        this._updateStepCircles(currentStep, effectiveCompleted, wasStep2Skipped);
        this._updateMobileView(currentStep);
    }

    /**
     * Updates the width of the progress bar line.
     * @private
     */
    _updateProgressLine(currentStep) {

        if (!this.progressLine) {
            return;
        }

        const totalSteps = 3;
        let progressPercent = 0;

        if (currentStep === 1) {
            progressPercent = (1 / totalSteps) * 100;
        } else if (currentStep === 2) {
            progressPercent = (2 / totalSteps) * 100;
        } else if (currentStep >= 3) {
            progressPercent = 100;
        }

        this.progressLine.style.width = `${progressPercent}%`;
    }

    /**
     * Updates the visual state of each step circle (number, checkmark, color).
     * @private
     */
    _updateStepCircles(currentStep, completedSteps, wasStep2Skipped) {
        const stepElements = this.progressContainer.querySelectorAll('[data-step]');

        stepElements.forEach(stepEl => {
            const stepNumber = parseInt(stepEl.getAttribute('data-step'), 10);
            const circle = stepEl.querySelector('.step-circle');
            const label = stepEl.querySelector('p');

            if (!circle || !label) return;

            const isCompleted = completedSteps.includes(stepNumber);
            const isCurrent = stepNumber === currentStep;
            const isDisabledBySkip = stepNumber === 2 && wasStep2Skipped;

            // Reset classes
            circle.classList.remove('bg-white', 'text-[#00B588]', 'bg-[#00B588]', 'text-white', 'border-gray', 'text-gray', 'border-green');
            label.classList.remove('text-[#00B588]', 'font-semibold', 'text-gray-700', 'text-gray');

            // Restore number from checkmark if needed
            if (circle.hasAttribute('data-step-number')) {
                const checkmark = circle.querySelector('svg');
                if (checkmark) checkmark.remove();
                circle.textContent = circle.getAttribute('data-step-number');
            }

            if (isCurrent) {
                // Style for the CURRENT step
                circle.classList.add('bg-white', 'text-[#00B588]');
                label.classList.add('text-[#00B588]', 'font-semibold');
                circle.style.cursor = 'default';
            } else if (isCompleted) {
                // Style for COMPLETED steps
                circle.classList.add('bg-[#00B588]', 'text-white', 'border-green');
                label.classList.add('text-gray-700', 'font-semibold');

                // Add checkmark icon
                if (!circle.hasAttribute('data-step-number')) {
                    circle.setAttribute('data-step-number', circle.textContent);
                }
                circle.innerHTML = `<svg class="h-8 w-8" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" /></svg>`;

                // Make clickable unless it's the disabled skipped step
                circle.style.cursor = isDisabledBySkip ? 'not-allowed' : 'pointer';
            } else {
                // Style for FUTURE steps
                circle.classList.add('bg-white', 'text-gray', 'border-gray');
                label.classList.add('text-gray');
                circle.style.cursor = 'not-allowed';
            }
        });
    }

    /**
     * Updates the mobile-specific progress view.
     * @private
     */
    _updateMobileView(currentStep) {
        const mobileContainer = this.progressContainer.querySelector('#mobile-current-step');
        if (mobileContainer) {
            mobileContainer.innerHTML = `<div class="text-sm text-gray-500 mt-2 p-2">Step ${currentStep} of 3</div>`;
        }

        const mobileBackBtn = this.progressContainer.querySelector('#mobile-back-btn');
        if (mobileBackBtn) {
            mobileBackBtn.classList.toggle('hidden', currentStep <= 1);
        }

        const balanceDiv = this.progressContainer.querySelector('#balance-div');
        if(balanceDiv) {
            balanceDiv.classList.toggle('hidden', currentStep === 1);
        }
    }
}
